// pages/master/menu/MasterMenuAddEdit.jsx
'use client';

import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Typography,
  Checkbox,
  IconButton,
  Button,
  FormGroup,
  Paper,
  Divider,
  Card,
  CardContent,
  Stack,
  Pagination,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import FormDialog from '../../../components/FormDialog';
import { zmdiToMuiIconMap } from '../../../utils/iconMapper';
import { createModuleMenu, updateModuleMenu } from '../../../stores/roleModuleStore';
import { useLanguage } from '../../../utils/i18n';
import { useNotifier } from '../../../components/Notifier';

const MasterMenuAddEdit = ({ open, onClose, isEdit = false, initialData = null, parentTargetItem = null, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  // State Form Default (menyesuaikan initialData jika dalam mode edit)
  const [formData, setFormData] = useState({
    modules_name: initialData?.modules_name || initialData?.name || '',
    alias: initialData?.alias || '',
    modules_path: initialData?.modules_path || '',
    modules_option: initialData?.modules_option || 'origin',
    is_active: initialData?.is_active ?? true,
    modules_icon: initialData?.modules_icon || '',
    features: {
      read: initialData?.features?.find((f) => f.name === 'read')?.status ?? true,
      create: initialData?.features?.find((f) => f.name === 'create')?.status ?? true,
      update: initialData?.features?.find((f) => f.name === 'edit' || f.name === 'update')?.status ?? true,
      delete: initialData?.features?.find((f) => f.name === 'delete')?.status ?? true,
      report: initialData?.features?.find((f) => f.name === 'report')?.status ?? true
    },
    id: initialData?.id || 0,
    idx1: initialData?.idx1 || 1,
    idx2: initialData?.idx2 || 1,
    idx3: initialData?.idx3 || 1,
    idx4: initialData?.idx4 || 1,
    idx5: initialData?.idx5 || 1,
    idx_level: initialData?.idx_level || 1,
    parent_name: initialData?.parent_name || 1
  });

  const [saving, setSaving] = useState(false);
  const [isPathEdited, setIsPathEdited] = useState(!!initialData?.modules_path);

  // --- Pagination & Search for Icons ---
  const [iconSearch, setIconSearch] = useState('');
  const [iconPage, setIconPage] = useState(1);
  const ICONS_PER_PAGE = 35;

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'modules_name') {
      setFormData((prev) => {
        const newData = { ...prev, modules_name: value };
        // Auto-fill path if it hasn't been manually edited and we are creating new
        if (!isPathEdited && !isEdit) {
          newData.modules_path = value
            .toLowerCase()
            .replace(/\s+/g, '-') // Replace spaces with dashes
            .replace(/[^a-z0-9-]/g, ''); // Remove invalid characters
        }
        return newData;
      });
    } else if (name === 'modules_path') {
      setIsPathEdited(true);
      // Validasi: hanya huruf, angka, dash, dan tidak ada spasi
      const validatedPath = value.replace(/[^a-zA-Z0-9-]/g, '');
      setFormData((prev) => ({ ...prev, modules_path: validatedPath }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFeatureChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, [name]: checked }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        modules_name: formData.modules_name,
        alias: formData.alias,
        modules_path: formData.modules_path,
        modules_option: formData.modules_option,
        is_active: formData.is_active,
        modules_icon: formData.modules_icon || 'zmdi-folder',
        features: [
          { name: 'read', status: formData.features.read },
          { name: 'create', status: formData.features.create },
          { name: 'edit', status: formData.features.update },
          { name: 'delete', status: formData.features.delete },
          { name: 'report', status: formData.features.report }
        ]
      };

      const updatePayload = {
        modules_name: formData.modules_name,
        alias: formData.alias,
        modules_path: formData.modules_path,
        modules_option: formData.modules_option,
        is_active: formData.is_active,
        modules_icon: formData.modules_icon || 'zmdi-folder',
        features: [
          { name: 'read', status: formData.features.read },
          { name: 'create', status: formData.features.create },
          { name: 'edit', status: formData.features.update },
          { name: 'delete', status: formData.features.delete },
          { name: 'report', status: formData.features.report }
        ],
        id: formData.id,
        idx1: formData.idx1,
        idx2: formData.idx2,
        idx3: formData.idx3,
        idx4: formData.idx4,
        idx5: formData.idx5,
        idx_level: formData.idx_level,
        parent_name: formData.parent_name
      };

      if (isEdit && initialData?.id) {
        updatePayload.id = initialData.id;
        await updateModuleMenu(updatePayload);
        notify({ message: t('masterMenu.menuUpdatedSuccess', 'Menu updated successfully'), severity: 'success' });
      } else {
        await createModuleMenu(payload);
        notify({ message: t('masterMenu.menuCreatedSuccess', 'The new menu has been successfully created'), severity: 'success' });
      }
      if (typeof onSuccess === 'function') {
        onSuccess(parentTargetItem);
      }

      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage =
        err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const SelectedIconComponent = zmdiToMuiIconMap[formData.modules_icon] || zmdiToMuiIconMap['default'];

  // Semantic search mapping
  const semanticKeywords = {
    vehicle: ['car', 'truck', 'bus', 'taxi', 'bike', 'motorcycle', 'shuttle', 'commute', 'rv', 'agriculture', 'fire-truck', 'shipping'],
    fleet: ['car', 'truck', 'bus', 'shipping', 'garage', 'repair'],
    fuel: ['gas', 'fuel', 'ev', 'oil', 'propane', 'battery', 'station'],
    maintenance: ['wrench', 'repair', 'crash', 'tire', 'handyman', 'engineering', 'construction', 'wash', 'build'],
    user: ['person', 'account', 'badge', 'people', 'employee', 'driver'],
    tracking: ['map', 'pin', 'location', 'gps', 'route', 'navigation', 'globe', 'polyline', 'explore'],
    sensor: ['speed', 'sensor', 'thermostat', 'memory', 'cam', 'dns', 'iot'],
    logistics: ['inventory', 'warehouse', 'receipt', 'shipping', 'box', 'package'],
    dashboard: ['dashboard', 'chart', 'assessment', 'timeline', 'table', 'report', 'analytics'],
    time: ['time', 'timer', 'schedule', 'update', 'history', 'alarm', 'calendar', 'clock'],
    setting: ['setting', 'gear', 'wrench']
  };

  const filteredIcons = useMemo(() => {
    let keys = Object.keys(zmdiToMuiIconMap).filter((k) => k !== 'default');
    if (iconSearch) {
      const lowerSearch = iconSearch.toLowerCase();
      // Find matching semantic categories
      const matchedCategories = Object.keys(semanticKeywords).filter((cat) => cat.includes(lowerSearch));

      let semanticMatches = [];
      matchedCategories.forEach((cat) => {
        semanticMatches = [...semanticMatches, ...semanticKeywords[cat]];
      });

      keys = keys.filter((key) => {
        // Direct string match
        if (key.toLowerCase().includes(lowerSearch)) return true;
        // Semantic match
        for (let word of semanticMatches) {
          if (key.toLowerCase().includes(word)) return true;
        }
        return false;
      });
    }
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconSearch]);

  const paginatedIcons = useMemo(() => {
    const start = (iconPage - 1) * ICONS_PER_PAGE;
    return filteredIcons.slice(start, start + ICONS_PER_PAGE);
  }, [filteredIcons, iconPage]);

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? t('masterMenu.editMenuConfiguration', 'Edit Menu Configuration') : t('masterMenu.createNewMenu', 'Create New Menu')}
      fullScreenMode={true}
      actions={
        <>
          <Button onClick={onClose} variant="outlined">
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || (!formData.modules_name && !formData.alias)}
            disableElevation
            sx={{
              backgroundColor: '#0072DA',
              textTransform: 'none',
              px: 3,
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(0, 114, 218, 0.2)',
              '&:hover': {
                backgroundColor: '#005bb5',
                boxShadow: '0 6px 20px rgba(0, 114, 218, 0.3)'
              }
            }}
          >
            {saving ? t('general.processing', 'Processing...') : t('general.save', 'Save')}
          </Button>
        </>
      }
    >
      <Box sx={{ flexGrow: 1, minHeight: '100%' }}>
        <Grid container spacing={3}>
          {/* LEFT COLUMN: Form Inputs */}
          <Grid size={{ xs: 12, md: 6, lg: 6 }}>
            <Stack spacing={3}>
              {/* Parent Target Alert (if adding child) */}
              {!isEdit && parentTargetItem && (
                <Box sx={{ p: 2, bgcolor: 'primary.main', color: '#ffffff', borderRadius: 2, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: 14 }}>
                    {t('masterMenu.addingNewSubMenuUnder', 'Adding new sub-menu under:')}{' '}
                    {parentTargetItem.alias || parentTargetItem.modules_name}
                  </Typography>
                </Box>
              )}

              {/* Basic Information Card */}
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="700" color="text.primary" gutterBottom>
                    {t('masterMenu.basicInformation', 'Basic Information')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('masterMenu.defineCoreIdentity', 'Define the core identity and routing for this menu item.')}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t('masterMenu.menuName', 'Menu Name')}
                        name="modules_name"
                        value={formData.modules_name}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder={t('masterMenu.egReportSummary', 'e.g. report-summary')}
                        disabled={isEdit}
                        helperText={isEdit ? t('masterMenu.menuNameCannotBeChanged', 'Menu Name cannot be changed in edit mode') : ''}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t('masterMenu.alias', 'Alias')}
                        name="alias"
                        value={formData.alias}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder={t('masterMenu.egDashboard', 'e.g. Dashboard')}
                      />
                    </Grid>
                    <Grid size={{ sm: 12 }}>
                      <TextField
                        fullWidth
                        label={t('masterMenu.routingPath', 'Routing Path')}
                        name="modules_path"
                        value={formData.modules_path}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder={t('masterMenu.egDashboardAsset', 'e.g. dashboard-asset')}
                        helperText={t('masterMenu.pathValidationHint', 'Only letters, numbers, and dashes (-) are allowed. No spaces.')}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Settings & Permissions Card */}
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="700" color="text.primary" gutterBottom>
                    {t('masterMenu.settingsAndPermissions', 'Settings & Permissions')}
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={4}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" fontWeight="600" color="text.secondary" gutterBottom>
                        {t('masterMenu.menuType', 'Menu Type')}
                      </Typography>
                      <RadioGroup row name="modules_option" value={formData.modules_option} onChange={handleChange}>
                        <FormControlLabel value="origin" control={<Radio color="primary" />} label={t('masterMenu.origin', 'Origin')} />
                        <FormControlLabel value="embed" control={<Radio color="primary" />} label={t('masterMenu.embedd', 'Embedd')} />
                      </RadioGroup>

                      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', p: 1.5, bgcolor: '#f0f4f8', borderRadius: 2 }}>
                        <Switch
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          color="primary"
                        />
                        <Typography ml={1} fontWeight="500" color={formData.is_active ? 'primary.main' : 'text.secondary'}>
                          {formData.is_active
                            ? t('masterMenu.menuIsActive', 'Menu is Active')
                            : t('masterMenu.menuIsInactive', 'Menu is Inactive')}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" fontWeight="600" color="text.secondary" gutterBottom>
                        {t('masterMenu.defaultFeaturesPermissions', 'Default Features / Permissions')}
                      </Typography>
                      <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.features.read}
                              onChange={handleFeatureChange}
                              name="read"
                              sx={{ color: '#2196f3', '&.Mui-checked': { color: '#2196f3' } }}
                            />
                          }
                          label={<Typography variant="body2">{t('masterMenu.read', 'Read')}</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.features.create}
                              onChange={handleFeatureChange}
                              name="create"
                              sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
                            />
                          }
                          label={<Typography variant="body2">{t('masterMenu.create', 'Create')}</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.features.update}
                              onChange={handleFeatureChange}
                              name="update"
                              sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }}
                            />
                          }
                          label={<Typography variant="body2">{t('masterMenu.update', 'Update')}</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.features.delete}
                              onChange={handleFeatureChange}
                              name="delete"
                              sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }}
                            />
                          }
                          label={<Typography variant="body2">{t('masterMenu.delete', 'Delete')}</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.features.report}
                              onChange={handleFeatureChange}
                              name="report"
                              sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                            />
                          }
                          label={<Typography variant="body2">{t('masterMenu.report', 'Report')}</Typography>}
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* RIGHT COLUMN: Icon Selection */}
          <Grid size={{ xs: 12, md: 6, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                height: { xs: '400px', md: '100%' },
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                bgcolor: '#ffffff'
              }}
            >
              {/* Selected Icon Preview Area */}
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'primary.50', // Very light blue background
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                <Box
                  sx={{
                    bgcolor: '#ffffff',
                    borderRadius: '50%',
                    width: 72,
                    height: 72,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    mb: 2
                  }}
                >
                  <SelectedIconComponent sx={{ fontSize: 36, color: 'primary.main' }} />
                </Box>
                <Typography variant="body2" color="text.secondary" textTransform="uppercase" letterSpacing={1}>
                  {t('masterMenu.selectedIcon', 'Selected Icon')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  {formData.modules_icon || t('masterMenu.none', 'None')}
                </Typography>
              </Box>

              {/* Icon Library Grid */}
              <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                  {t('masterMenu.chooseFromLibrary', 'Choose from Library')} ({filteredIcons.length} icons)
                </Typography> */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('masterMenu.searchIconPlaceholder', 'Search (e.g., "vehicle", "fuel", "setting")')}
                  value={iconSearch}
                  onChange={(e) => {
                    setIconSearch(e.target.value);
                    setIconPage(1);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{ bgcolor: '#f8fafc' }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
                  gap: 1.5,
                  alignContent: 'start',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '4px' }
                }}
              >
                {paginatedIcons.map((key) => {
                  const IconCmp = zmdiToMuiIconMap[key];
                  const isSelected = formData.modules_icon === key;

                  return (
                    <IconButton
                      key={key}
                      onClick={() => setFormData((prev) => ({ ...prev, modules_icon: key }))}
                      sx={{
                        borderRadius: 2,
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : '#e0e0e0',
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.100' : '#f5f5f5',
                          borderColor: isSelected ? 'primary.main' : '#bdbdbd'
                        }
                      }}
                      title={key}
                    >
                      <IconCmp fontSize="small" />
                    </IconButton>
                  );
                })}
              </Box>

              {/* Pagination */}
              {filteredIcons.length > ICONS_PER_PAGE && (
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={Math.ceil(filteredIcons.length / ICONS_PER_PAGE)}
                    page={iconPage}
                    onChange={(_, page) => setIconPage(page)}
                    color="primary"
                    size="small"
                    siblingCount={1}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </FormDialog>
  );
};

export default MasterMenuAddEdit;
